/**
 * LibrarianMentor — the Socratic mentor side panel.
 *
 * Activates after sign-in. Floats as a calm widget in the corner of the
 * authenticated experience. User can open it, dismiss it, or it disappears
 * once they've completed onboarding milestones.
 *
 * Methodology hold #5: the Librarian leaves the user with a clearer head.
 * Hold #1: AI moderates behavior, not ideas.
 *
 * Tone signals (Lead's brief): warm, librarian-quiet, mentor not vending
 * machine. Asks more than it answers.
 */
import { useEffect, useRef, useState } from 'react';
import { BookOpen, Send, X, Loader2, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY_DISMISSED = 'tsogn:librarian_dismissed_v1';
const STORAGE_KEY_OPEN = 'tsogn:librarian_open_v1';

export default function LibrarianMentor() {
  const { user } = useAuth();
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_OPEN) === '1';
    } catch { return false; }
  });
  const [seeded, setSeeded] = useState(false);
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_DISMISSED) === '1'; } catch { return false; }
  });
  const scrollRef = useRef(null);

  // Persist open state — must run before any conditional returns (hooks order)
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_OPEN, open ? '1' : '0'); } catch {/* ignore */}
  }, [open]);

  // Seed welcome message when first opened
  useEffect(() => {
    if (!open || seeded || !user) return;
    (async () => {
      setBusy(true);
      try {
        const r = await api.post('/librarian/welcome');
        setSessionId(r.data.session_id);
        if (!r.data.is_new) {
          const h = await api.get('/librarian/history');
          setTurns(h.data.turns || []);
        } else {
          setTurns([{ role: 'assistant', text: r.data.first_message, created_at: new Date().toISOString() }]);
        }
        setSeeded(true);
      } catch {
        setTurns([{
          role: 'assistant',
          text: 'Welcome. I\'m the Librarian. I\'m having a slow start — but ask me anything and I\'ll catch up.',
          created_at: new Date().toISOString(),
        }]);
        setSeeded(true);
      }
      setBusy(false);
    })();
  }, [open, seeded, user]);

  // Auto-scroll to latest turn
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns]);

  // Don't render if no auth
  if (!user) return null;

  // Don't render if user has explicitly dismissed
  if (dismissed) return null;
  if (user?.librarian_dismissed_at) return null;

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setDraft('');
    const newUserTurn = { role: 'user', text, created_at: new Date().toISOString() };
    setTurns((prev) => [...prev, newUserTurn]);
    try {
      const r = await api.post('/librarian/message', {
        message: text,
        session_id: sessionId,
      });
      if (!sessionId) setSessionId(r.data.session_id);
      setTurns((prev) => [...prev, {
        role: 'assistant', text: r.data.reply, created_at: new Date().toISOString(),
      }]);
    } catch (e) {
      setTurns((prev) => [...prev, {
        role: 'assistant',
        text: e.response?.data?.detail || 'I lost the thread for a moment. Try again?',
        created_at: new Date().toISOString(),
      }]);
    }
    setBusy(false);
  };

  const dismissForever = async () => {
    try {
      await api.post('/librarian/dismiss-onboarding');
      localStorage.setItem(STORAGE_KEY_DISMISSED, '1');
    } catch {/* ignore */}
    setOpen(false);
    setDismissed(true);
  };

  // ─── Closed state — small floating button ───
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="librarian-launch-btn"
        className="fixed bottom-20 right-5 z-50 inline-flex items-center gap-2 rounded-full px-4 py-3 border border-[#D4A84A]/40 bg-[#0F0F11]/95 text-[#D4A84A] backdrop-blur-md hover:bg-[#D4A84A]/10 transition-colors shadow-xl"
        aria-label="Open the Librarian"
      >
        <BookOpen size={16} />
        <span className="font-hud text-[10px] md:text-xs tracking-widest uppercase">The Librarian</span>
      </button>
    );
  }

  // ─── Open state — slide-out panel ───
  return (
    <div
      className="fixed bottom-20 right-5 z-50 w-[min(92vw,420px)] max-h-[80vh] flex flex-col rounded-md border border-[#D4A84A]/30 bg-[#0F0F11]/97 backdrop-blur-md shadow-2xl"
      data-testid="librarian-panel"
    >
      <div className="px-4 py-3 border-b border-[#D4A84A]/15 flex items-center gap-2">
        <BookOpen size={14} className="text-[#D4A84A]" />
        <div className="flex-1 min-w-0">
          <div className="font-hud text-[10px] md:text-xs tracking-widest text-[#D4A84A] uppercase">The Librarian</div>
          <div className="font-mono text-[10px] text-white/45 mt-0.5">A mentor — not a vending machine</div>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="librarian-close-btn"
          aria-label="Close"
          className="text-white/45 hover:text-white p-1"
        >
          <X size={14} />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 max-h-[55vh]" data-testid="librarian-turns">
        {turns.length === 0 && busy && (
          <div className="flex items-center gap-2 font-mono text-xs text-white/55">
            <Loader2 size={12} className="animate-spin" /> Listening…
          </div>
        )}
        {turns.map((t, i) => (
          <div
            key={i}
            data-testid={`librarian-turn-${t.role}`}
            className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-3 py-2 rounded-md font-mono text-xs md:text-sm leading-relaxed ${
                t.role === 'user'
                  ? 'bg-[#2F8A92]/10 border border-[#2F8A92]/25 text-white/90'
                  : 'bg-[#D4A84A]/06 border border-[#D4A84A]/20 text-white/90'
              }`}
            >
              {t.text}
            </div>
          </div>
        ))}
        {busy && turns.length > 0 && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-3 py-2 rounded-md bg-[#D4A84A]/06 border border-[#D4A84A]/20 inline-flex items-center gap-2 font-mono text-xs text-white/55">
              <Loader2 size={11} className="animate-spin text-[#D4A84A]" /> The Librarian is thinking
            </div>
          </div>
        )}
      </div>

      <form onSubmit={sendMessage} className="px-4 pb-3 pt-1 border-t border-[#D4A84A]/15" data-testid="librarian-form">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask the Librarian. Try: 'Where do I start?'"
            rows={2}
            disabled={busy}
            data-testid="librarian-input"
            className="flex-1 bg-[#0A0A0A] border border-[#D4A84A]/20 rounded px-2 py-1.5 font-mono text-xs md:text-sm text-white/90 outline-none focus:border-[#D4A84A]/55 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            data-testid="librarian-send-btn"
            className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <Send size={11} />
          </button>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="font-mono text-[9px] text-white/35">Enter to send · Shift+Enter for newline</span>
          <button
            type="button"
            onClick={dismissForever}
            data-testid="librarian-dismiss-forever"
            className="font-mono text-[9px] text-white/35 hover:text-white/65 underline underline-offset-2"
          >
            I&apos;ve got it from here
          </button>
        </div>
      </form>
    </div>
  );
}
